import React, { useEffect, useState } from 'react';
import {
  Box, IconButton, Tooltip, Chip, Typography, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NodeSyncResult from '../../../components/NodeSyncResult';
import TaskProgress from '../../../components/TaskProgress';
import { format } from 'date-fns';
import Datatable from '../../../components/Datatable';
import { useNotify } from '../../../components/Datatable/NotifyProvider';
import { useAuth } from '../../../components/AuthProvider';
import { useLoading } from '../../../components/LoadingProvider';
import api from '../../../services/api';

// Icons
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';

import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';

// Components
import DomainForm from './DomainForm';
import { ToggleOff, ToggleOn } from '@mui/icons-material';
import Swal from 'sweetalert2';

export default function AllDomainsTable({ refresh, onRefresh }) {
  const notify = useNotify();
  const auth = useAuth();
  const loading = useLoading();
  const { isLoading, setLoading } = loading;
  
  const [formState, setFormState] = useState({
    open: false,
    id: null
  });
  const [domains, setCategories] = useState([]);
  const [categoryInfo, setCategoryInfo] = useState({
    open: false,
    name: '',
    info: null
  });
  const [bulkConfirm, setBulkConfirm] = useState({ open: false, items: [], setSelectedRows: null });
  const [syncResult, setSyncResult] = useState({ open: false, nodes: [], summary: null });
  const [taskProgress, setTaskProgress] = useState({ open: false, taskId: null, title: '' });

  // Carregar categorias
  const fetchCategories = async () => {
    try {
      const response = await api.post('/dns/domains', {
        params: {
          limit: 100, // Solicitar bastante categorias para o dropdown
          offset: 0,
          sort_field: 'name',
          sort_direction: 'asc'
        }
      });
      if (response.data) {
        console.log(response.data);
        setCategories(response.data || []);
      }
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
    }
  };

  // Carregar dados ao iniciar
  // useEffect(() => {
  //   fetchCategories();
  // }, [refresh]);

  // Função para mostrar info da categoria
  const showCategoryInfo = (name, info) => {
    setCategoryInfo({
      open: true,
      name,
      info
    });
  };

  // Função para fechar detalhes da categoria
  const closeCategoryInfo = () => {
    setCategoryInfo({
      open: false,
      name: '',
      info: null
    });
  };

  // Função para ativar/desativar domínio
  const toggleDomainActive = async (domainId, currentStatus) => {
    try {
      setLoading(true);
      const response = await api.put(`/dns/blacklist/domains/${domainId}`, {
        is_active: !currentStatus
      });
      
      if (response.data.success) {
        notify.setNotify({
          open: true,
          message: response.data.success,
          severity: 'success'
        });
        
        // Exibir resultado dos nós DNS, se houver
        if (response.data.nodes && response.data.nodes.length > 0) {
          const hasErrors = response.data.nodes.some(node => !node.result);
          const nodesMessage = response.data.nodes
            .map(node => `${node.name}: ${node.result ? 'Sucesso' : 'Falha'}`)
            .join('\n');
          
          notify.setNotify({
            open: true,
            message: nodesMessage,
            severity: hasErrors ? 'warning' : 'success'
          });
        }
        
        // Recarregar os dados
        if (onRefresh) onRefresh();
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao atualizar status do domínio',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para excluir um domínio
  const deleteDomain = async (domain,category) => {
    try {
      setLoading(true);
      const response = await api.delete(`/dns/blacklist/categories/${category}/domains/${domain}`);
      
      if (response.data.nodes) {
        const hasErrors = response.data.nodes.some(node => !node.result);
        const nodesMessage = response.data.nodes
          .map(node => `${node.name}: ${node.result ? 'Sucesso' : 'Falha'}`)
          .join('\n');
        
        notify.setNotify({
          open: true,
          message: response.data.message || nodesMessage,
          severity: hasErrors ? 'warning' : 'success'
        });
        
        // Recarregar os dados
        if (onRefresh) onRefresh();
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao excluir domínio',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Bulk delete de domínios — recebe array de IDs (não strings).
  const deleteMultipleDomainsByIds = async (ids) => {
    try {
      setLoading(true);
      const response = await api.post('/dns/blacklist/domains/delete', { ids });

      if (response.data?.error) {
        notify.setNotify({ open: true, message: response.data.error, severity: 'error' });
      } else {
        const removed = response.data?.removed ?? ids.length;
        const taskId = response.data?.task_id;
        // Resposta 202 com task_id — DB já atualizou, sync DNS está na fila.
        // Abre TaskProgress que polla até concluir.
        notify.setNotify({
          open: true,
          message: `${removed} domínio(s) removido(s) do banco. Sincronizando DNS em background...`,
          severity: 'info',
        });
        if (taskId) {
          setTaskProgress({
            open: true,
            taskId,
            title: `Sincronização DNS após remoção de ${removed} domínio(s)`,
          });
        }
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao excluir domínios',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const addDomains = async (formData) => {
    try {
      setLoading(true);
      
      // Verificar se temos algum conteúdo (texto ou arquivo)
      const hasBlacklist = formData.get('blacklist') && formData.get('blacklist').trim() !== '';
      const hasFile = formData.get('file') !== null;
      
      if (!hasBlacklist && !hasFile) {
        notify.setNotify({
          open: true,
          message: 'Nenhum domínio ou arquivo informado',
          severity: 'warning'
        });
        return;
      }
      
      // Endpoint correto: /dns/blacklist/categories/add_domains
      const response = await api.post('/dns/blacklist/categories/add_domains', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const added = response.data?.added_domains || 0;
      const rejected = response.data?.rejected || [];
      const taskId = response.data?.task_id;

      if (added > 0) {
        // Fechou form + abre HUD se houve enqueue
        if (taskId) {
          try { window.dispatchEvent(new Event('task-enqueued')); } catch (_) {}
          setTaskProgress({
            open: true,
            taskId,
            title: `Sincronização DNS após adicionar ${added} domínio(s)`,
          });
        }
        notify.setNotify({
          open: true,
          message: `${added} domínio(s) adicionado(s)${rejected.length ? ` · ${rejected.length} rejeitado(s)` : ''}${taskId ? ' — sync na fila' : ''}`,
          severity: 'success',
        });
        setFormState({ open: false, id: null });
        if (onRefresh) onRefresh();
      } else if (response.data?.error) {
        notify.setNotify({ open: true, message: response.data.error, severity: 'error' });
      } else {
        notify.setNotify({
          open: true,
          message: rejected.length ? `Nenhum domínio aceito · ${rejected.length} rejeitado(s)` : 'Nenhum domínio adicionado',
          severity: 'warning',
        });
      }
    } catch (error) {
      console.error("Erro ao adicionar domínios:", error);
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || error.response?.data?.detail || 'Erro ao adicionar domínios',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Form de domínios */}
      <DomainForm
        open={formState.open}
        id={formState.id}
        categories={domains}
        onClose={() => setFormState({ open: false, id: null })}
        onSubmit={addDomains}
      />
      
      {/* Diálogo de detalhes da categoria */}
      <Dialog
        open={categoryInfo.open}
        onClose={closeCategoryInfo}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Detalhes da Categoria: {categoryInfo.name}</DialogTitle>
        <DialogContent>
          {categoryInfo.info && (
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Status" 
                  secondary={categoryInfo.info.is_active ? "Ativa" : "Inativa"} 
                />
                <Chip 
                  size="small" 
                  color={categoryInfo.info.is_active ? "success" : "default"}
                  label={categoryInfo.info.is_active ? "Ativa" : "Inativa"}
                />
              </ListItem>
              {categoryInfo.info.expired_at && (
                <ListItem>
                  <ListItemText 
                    primary="Data de Expiração" 
                    secondary={format(new Date(categoryInfo.info.expired_at), 'dd/MM/yyyy')} 
                  />
                </ListItem>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCategoryInfo}>Fechar</Button>
        </DialogActions>
      </Dialog>
      
      {/* Tabela de domínios */}
      <Datatable
        refresh={refresh}
        handlerRefresh={onRefresh}
        title="Todos os Domínios Bloqueados"
        endpoint="/dns/blacklist/domains"
        options={{
          customToolbar: () =>
            <>
              <Tooltip title="Remover todos os filtros">
                <IconButton onClick={() => { localStorage.removeItem("datatable/dns/blacklist/domains"); window.location.reload() }} >
                  <RemoveCircleOutline />
                </IconButton>
              </Tooltip>
              {/* <Tooltip title="Adicionar Domínios">
                <IconButton 
                  disabled={!auth.hasPerm("dns_blacklist.create")}
                  onClick={() => setFormState({ open: true, id: null })}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip> */}
            </>
          ,
          selectableRows: "multiple",
          customToolbarSelect: (selectedRows, displayData, setSelectedRows) =>
            <Tooltip title="Remover domínios selecionados">
              <IconButton
                disabled={!auth.hasPerm('dns_blacklist.delete')}
                onClick={() => {
                  // Mapeia (id, domínio, categoria) — id=column[0], domain=column[1], category=column[2]
                  const items = selectedRows.data.map((row) => ({
                    id: displayData[row.dataIndex].data[0],
                    domain: displayData[row.dataIndex].data[1],
                    category: displayData[row.dataIndex].data[2],
                  }));
                  setBulkConfirm({ open: true, items, setSelectedRows });
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
        }}
        columns={[
          {
            name: 'id',
            label: 'ID',
            options: {
              display: false
            }
          },
          {
            name: 'domain',
            label: 'Domínio',
          },
          {
            name: 'category',
            label: 'Categoria',
            options: {
              customBodyRender: (value, tableMeta) => {
                const isActive = tableMeta.rowData[3]; // campo "status"
                return (
                  <Chip
                  label={value}
                  size="small"
                  variant="outlined"
                  color={isActive ? "primary" : "error"}
                  />
                );
                }
              }
              },
              {
              name: 'status',
              label: 'Status',
              options: {
                customBodyRender: (value) => (
                <Chip 
                  label={value === true ? 'Ativo' : 'Inativo'}
                  size="small"
                  color={value === true ? 'success' : 'default'}
                />
                )
              }
              },
              {
              name: 'dns_list',
              label: 'Ativo',
              options: {
                display: false
              }
              },
              {
              name: 'actions',
              label: 'Ações',
              options: {
                filter: false,
                sort: false,
                customBodyRender: (value, tableMeta) => {
                const domainId = tableMeta.rowData[0];
                const domain = tableMeta.rowData[1];
                const isActive = tableMeta.rowData[5];
                const categoryId = tableMeta.rowData[4];
                
                return (
                  <Box>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        disabled={!auth.hasPerm("dns_blacklist.delete")}
                        onClick={() => {
                          Swal.fire({
                            title: 'Tem certeza?',
                            text: "Você não poderá reverter isso!",
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonText: 'Sim, excluir!',
                            cancelButtonText: 'Cancelar'
                          }).then((result) => {
                            if (result.isConfirmed) {
                              deleteDomain(domainId, categoryId);
                            }
                          });
                         
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              }
            }
          }
        ]}
      />

      {/* Status de sincronização nos nós DNS após operação síncrona (legacy) */}
      <NodeSyncResult
        open={syncResult.open}
        nodes={syncResult.nodes}
        summary={syncResult.summary}
        title="Sincronização DNS — Bulk Delete"
        onClose={() => setSyncResult({ open: false, nodes: [], summary: null })}
      />

      {/* Progresso da task de sincronização DNS na fila */}
      <TaskProgress
        open={taskProgress.open}
        taskId={taskProgress.taskId}
        title={taskProgress.title}
        onClose={() => setTaskProgress({ open: false, taskId: null, title: '' })}
        onComplete={() => onRefresh && onRefresh()}
      />

      {/* Dialog de confirmação do bulk delete (substitui window.confirm nativo) */}
      <Dialog
        open={bulkConfirm.open}
        onClose={() => setBulkConfirm({ open: false, items: [], setSelectedRows: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Remover {bulkConfirm.items.length} domínio(s)?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Esta ação vai desativar os domínios selecionados e sincronizar os servidores DNS. A operação pode ser revertida via auditoria.
          </Typography>
          <List dense sx={{ maxHeight: 280, overflow: 'auto', bgcolor: 'background.default', borderRadius: 1 }}>
            {bulkConfirm.items.slice(0, 50).map((it) => (
              <ListItem key={it.id} sx={{ py: 0.25 }}>
                <ListItemText primary={it.domain} secondary={it.category || '—'} />
              </ListItem>
            ))}
            {bulkConfirm.items.length > 50 && (
              <ListItem sx={{ py: 0.25 }}>
                <ListItemText primary={`... e mais ${bulkConfirm.items.length - 50} domínio(s)`} />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirm({ open: false, items: [], setSelectedRows: null })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              const ids = bulkConfirm.items.map(i => i.id);
              const reset = bulkConfirm.setSelectedRows;
              setBulkConfirm({ open: false, items: [], setSelectedRows: null });
              if (reset) reset([]);
              deleteMultipleDomainsByIds(ids);
            }}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}